import { useEffect, useState } from 'react'
import { portalApi } from '../../services/api'
import PortalShell from '../setup/PortalShell'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Embed Code Generator — Phase 9 of the Stay & Play portal.
 *
 * Shows the merchant:
 *   1. A full live preview of their widget in an iframe
 *   2. The iframe code to copy-paste onto their website
 *   3. The direct URL to copy
 *   4. Clear step-by-step instructions for WordPress, Squarespace, Wix, and raw HTML
 */
export default function EmbedCode() {
  const { t } = useLanguage()
  const [course, setCourse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(null) // 'iframe' | 'url' | null

  useEffect(() => {
    portalApi.get('/auth/me')
      .then(({ data }) => setCourse(data.course))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Build the embed URL from the course's TableKey
  // Tournament-API serves the standalone widget at /embed/stay-play/:courseKey
  // The embed endpoint lives on Tournament-API. For the PREVIEW iframe,
  // use a relative URL so Vite's proxy forwards it (avoids cross-port
  // "refused to connect" in the browser). For the COPYABLE code snippet
  // the merchant pastes on their own website, use the full Tournament-API
  // host so it works on any domain.
  const courseKey = course?.TableKey || course?.ID_NO || ''
  const previewUrl = courseKey ? `/embed/stay-play/${courseKey}` : ''
  const productionHost = import.meta.env.VITE_API_ORIGIN || window.location.origin
  const embedUrl = courseKey ? `${productionHost}/embed/stay-play/${courseKey}` : ''
  const iframeCode = embedUrl
    ? `<iframe src="${embedUrl}" width="100%" height="800" frameborder="0" style="border:none;max-width:1200px;"></iframe>`
    : ''

  function copyToClipboard(text, label) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label)
      setTimeout(() => setCopied(null), 2500)
    }).catch(() => {
      // Fallback for older browsers
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      setCopied(label)
      setTimeout(() => setCopied(null), 2500)
    })
  }

  return (
    <PortalShell>
      <div style={{ maxWidth: 1100, margin: '40px auto', padding: '0 20px' }}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: 0, fontSize: 28, color: 'var(--secondary-950)' }}>{t('sp_embed_title', 'Embed your widget')}</h1>
          <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 15 }}>
            {t('sp_embed_subtitle', 'Copy the code below and paste it into your website. Your Stay & Play packages will appear exactly as shown in the preview.')}
          </p>
        </div>

        {loading && (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading_ellipsis', 'Loading...')}</div>
        )}

        {!loading && !courseKey && (
          <div className="card" style={{ padding: 40, textAlign: 'center' }}>
            <p style={{ color: '#dc2626' }}>{t('sp_embed_no_course', 'Could not load your course. Make sure you\'ve completed the location setup first.')}</p>
          </div>
        )}

        {!loading && courseKey && (
          <>
            {/* ── Step 1: Copy the code ── */}
            <div className="card" style={{ padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, color: 'var(--secondary-950)' }}>{t('sp_embed_step1', '1. Copy the embed code')}</h3>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-muted)' }}>
                {t('sp_embed_step1_desc', 'Paste this into any HTML page, WordPress post, Squarespace code block, or Wix embed element.')}
              </p>

              {/* iframe code */}
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <pre style={{
                  background: '#1e293b', color: '#e2e8f0', padding: 16, borderRadius: 8,
                  fontSize: 12, lineHeight: 1.5, overflowX: 'auto', fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>
                  {iframeCode}
                </pre>
                <button
                  onClick={() => copyToClipboard(iframeCode, 'iframe')}
                  style={{
                    position: 'absolute', top: 10, right: 10,
                    padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                    background: copied === 'iframe' ? '#16a34a' : '#fff',
                    color: copied === 'iframe' ? '#fff' : '#1e293b',
                    border: '1px solid ' + (copied === 'iframe' ? '#16a34a' : '#cbd5e1'),
                    cursor: 'pointer',
                  }}
                >
                  {copied === 'iframe' ? t('sp_copied_check', '✓ Copied!') : t('sp_copy_code', 'Copy code')}
                </button>
              </div>

              {/* Direct URL */}
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                {t('sp_embed_or_share_direct_link', 'Or share the direct link:')}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  readOnly
                  value={embedUrl}
                  onClick={e => e.target.select()}
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--card-border)', fontSize: 12,
                    fontFamily: 'monospace', background: '#f8fafc',
                  }}
                />
                <button
                  onClick={() => copyToClipboard(embedUrl, 'url')}
                  style={{
                    padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    background: copied === 'url' ? '#16a34a' : '#fff',
                    color: copied === 'url' ? '#fff' : 'var(--secondary-950)',
                    border: '1px solid ' + (copied === 'url' ? '#16a34a' : 'var(--card-border)'),
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  {copied === 'url' ? t('sp_copied_check', '✓ Copied!') : t('sp_copy_url', 'Copy URL')}
                </button>
              </div>
            </div>

            {/* ── Step 2: Platform-specific instructions ── */}
            <div className="card" style={{ padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16, color: 'var(--secondary-950)' }}>{t('sp_embed_step2', '2. Paste it on your website')}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <InstructionBlock
                  platform="WordPress"
                  icon="W"
                  steps={[
                    t('sp_wp_step1', 'Edit the page where you want the widget to appear'),
                    t('sp_wp_step2', 'Add a "Custom HTML" block (or switch to the Code Editor)'),
                    t('sp_wp_step3', 'Paste the iframe code above'),
                    t('sp_wp_step4', 'Update/Publish the page'),
                  ]}
                />
                <InstructionBlock
                  platform="Squarespace"
                  icon="S"
                  steps={[
                    t('sp_sq_step1', 'Edit the page and click "Add Block"'),
                    t('sp_sq_step2', 'Choose "Code" under the "More" section'),
                    t('sp_sq_step3', 'Paste the iframe code into the code field'),
                    t('sp_sq_step4', 'Toggle off "Display Source" and save'),
                  ]}
                />
                <InstructionBlock
                  platform="Wix"
                  icon="X"
                  steps={[
                    t('sp_wix_step1', 'Open the Wix Editor and click "Add" (+)'),
                    t('sp_wix_step2', 'Select "Embed Code" then "Embed a Widget"'),
                    t('sp_wix_step3', 'Click "Enter Code" and paste the iframe code'),
                    t('sp_wix_step4', 'Resize the element to fit your page layout'),
                  ]}
                />
                <InstructionBlock
                  platform={t('sp_raw_html', 'Raw HTML')}
                  icon="<>"
                  steps={[
                    t('sp_html_step1', 'Open your HTML file in a text editor'),
                    t('sp_html_step2', 'Paste the iframe code where you want the widget'),
                    t('sp_html_step3', 'Adjust the width, height, and max-width as needed'),
                    t('sp_html_step4', 'Upload and refresh the page'),
                  ]}
                />
              </div>
            </div>

            {/* ── Step 3: Live preview ── */}
            <div className="card" style={{ padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, color: 'var(--secondary-950)' }}>{t('sp_embed_step3_preview', '3. Preview')}</h3>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-muted)' }}>
                {t('sp_embed_preview_desc', 'This is exactly what your visitors will see when they load the page.')}
              </p>
              <div style={{
                border: '2px solid var(--card-border)', borderRadius: 10,
                overflow: 'hidden', background: '#fff',
              }}>
                {/* Browser chrome mockup */}
                <div style={{
                  padding: '8px 14px', background: '#f1f5f9', borderBottom: '1px solid var(--card-border)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fca5a5' }} />
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fde68a' }} />
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#86efac' }} />
                  </div>
                  <div style={{
                    flex: 1, padding: '4px 10px', background: '#fff', borderRadius: 4,
                    fontSize: 11, color: '#64748b', fontFamily: 'monospace',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {embedUrl}
                  </div>
                </div>
                {/* Actual iframe */}
                <iframe
                  src={previewUrl}
                  title="Stay & Play Widget Preview"
                  style={{
                    width: '100%', height: 700, border: 'none', display: 'block',
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, fontStyle: 'italic' }}>
                {t('sp_embed_live_note', 'Changes to your packages and branding are reflected here instantly — no need to update the embed code.')}
              </div>
            </div>

            {/* ── Tips ── */}
            <div style={{
              padding: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
              marginBottom: 20, fontSize: 13, color: '#166534',
            }}>
              <strong>{t('sp_good_to_know', 'Good to know:')}</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20, lineHeight: 1.7 }}>
                <li>{t('sp_embed_tip_no_changes', 'The embed code never changes — you paste it once and it always shows your latest packages and branding.')}</li>
                <li>{t('sp_embed_tip_responsive', 'The widget is responsive and adjusts to the container width automatically.')}</li>
                <li>{t('sp_embed_tip_adjust_height', 'You can adjust the height in the iframe code if your packages need more or less space.')}</li>
                <li>{t('sp_embed_tip_powered_by', 'The widget includes a "Powered by InteGolf" link at the bottom.')}</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </PortalShell>
  )
}

function InstructionBlock({ platform, icon, steps }) {
  // Don't useLanguage here — platform name + step strings are passed in
  // already translated (or English fallback) from the parent EmbedCode call.
  const [open, setOpen] = useState(false)
  return (
    <div style={{
      border: '1px solid var(--card-border)', borderRadius: 8,
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
          background: open ? 'var(--brand-50)' : '#fff',
          border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{
          width: 28, height: 28, borderRadius: 6,
          background: 'var(--secondary-950)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 800, flexShrink: 0,
        }}>
          {icon}
        </span>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--secondary-950)' }}>
          {platform}
        </span>
        <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>
          {open ? '−' : '+'}
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 16px 16px 54px' }}>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#475569', lineHeight: 1.8 }}>
            {steps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}
    </div>
  )
}
